import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { tarotService } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import {
  ArrowLeftIcon,
  SparklesIcon,
  ClockIcon,
  EyeIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import { TarotReading } from '../types';

const HistoryPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [readings, setReadings] = useState<TarotReading[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedReading, setSelectedReading] = useState<TarotReading | null>(null);

  const themes = {
    amor: { name: 'Amor', icon: '💕', color: 'from-pink-500 to-rose-500' },
    saude: { name: 'Saúde', icon: '🌿', color: 'from-green-500 to-emerald-500' },
    dinheiro: { name: 'Dinheiro', icon: '💰', color: 'from-yellow-500 to-amber-500' }
  };

  const moods: Record<string, { name: string; icon: string }> = {
    grateful: { name: 'Grato(a)', icon: '🙏' },
    peaceful: { name: 'Tranquilo(a)', icon: '🕊️' },
    energetic: { name: 'Energético(a)', icon: '⚡' },
    reflective: { name: 'Reflexivo(a)', icon: '🤔' },
    anxious: { name: 'Ansioso(a)', icon: '😰' },
    hopeful: { name: 'Esperançoso(a)', icon: '🌟' },
  };

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      setLoading(true);
      const history = await tarotService.getReadingHistory();
      setReadings(history);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
      setReadings([
        {
          id: 1,
          theme: 'amor',
          spread_type: 'tres_cartas',
          question: 'Consulta de Amor',
          cards: [
            {
              id: 1,
              name: 'O Sol',
              meaning: {
                upright: 'Alegria, sucesso, vitalidade',
                reversed: 'Pessimismo, falta de energia'
              },
              interpretation: 'Um período de grande felicidade e realização está chegando em sua vida amorosa.',
              reversed: false
            },
            {
              id: 2,
              name: 'A Lua',
              meaning: {
                upright: 'Intuição, mistério, ilusões',
                reversed: 'Confusão, medos, ansiedade'
              },
              interpretation: 'Confie em sua intuição no presente momento, mas cuidado com falsas esperanças.',
              reversed: false
            },
            {
              id: 3,
              name: 'Os Enamorados',
              meaning: {
                upright: 'Amor, escolhas, união',
                reversed: 'Desarmonia, escolhas erradas'
              },
              interpretation: 'Uma decisão importante sobre relacionamentos se aproxima em seu futuro.',
              reversed: false
            }
          ],
          interpretation: 'Sua jornada amorosa mostra uma evolução positiva. O passado trouxe alegria, o presente pede intuição e o futuro reserva uma escolha importante que pode levar à união verdadeira.',
          created_at: '2024-01-15T10:30:00'
        },
        {
          id: 2,
          theme: 'dinheiro',
          spread_type: 'uma_carta',
          question: 'Consulta de Dinheiro',
          cards: [
            {
              id: 4,
              name: 'O Mago',
              meaning: {
                upright: 'Poder pessoal, manifestação, recursos',
                reversed: 'Manipulação, falta de foco'
              },
              interpretation: 'Você tem todas as ferramentas necessárias para alcançar a prosperidade financeira. É hora de agir com confiança.',
              reversed: false
            }
          ],
          interpretation: 'O Mago indica que você possui o poder e os recursos necessários para transformar sua situação financeira. Confie em suas habilidades e tome ação.',
          created_at: '2024-01-10T14:20:00'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: string | undefined) => {
    if (!date) return 'Data não disponível';
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(date));
  };

  const getSpreadName = (spreadType: string) => {
    const spreads: { [key: string]: string } = {
      'uma_carta': 'Uma Carta',
      'tres_cartas': 'Três Cartas',
      'cruz_celta': 'Cruz Celta'
    };
    return spreads[spreadType] || spreadType;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-mystical flex items-center justify-center">
        <LoadingSpinner size="lg" text="Carregando histórico..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-mystical">
      {/* Header */}
      <header className="bg-black/20 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center space-x-2 sm:space-x-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center space-x-2 text-white hover:text-gold-400 transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5" />
              <span>Voltar</span>
            </button>
            <div className="flex items-center space-x-2 sm:space-x-3">
              <ClockIcon className="w-5 h-5 sm:w-6 sm:h-6 text-mystical-400" />
              <h1 className="text-base sm:text-xl font-mystical font-bold text-white">Histórico de Consultas</h1>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-12">
        {selectedReading ? (
          // Visualização detalhada da consulta
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 sm:mb-8">
              <button
                onClick={() => setSelectedReading(null)}
                className="flex items-center space-x-2 text-white hover:text-gold-400 transition-colors"
              >
                <ArrowLeftIcon className="w-5 h-5" />
                <span>Voltar ao histórico</span>
              </button>
              <div className="sm:text-right">
                <p className="text-gray-300 text-sm">
                  {formatDate(selectedReading.created_at)}
                </p>
              </div>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-8 sm:mb-12">
                <div className="flex items-center justify-center mb-4">
                  <span className="text-3xl sm:text-4xl mr-3">
                    {themes[selectedReading.theme as keyof typeof themes]?.icon}
                  </span>
                  <h2 className="text-2xl sm:text-4xl font-mystical font-bold text-white">
                    {themes[selectedReading.theme as keyof typeof themes]?.name}
                  </h2>
                </div>
                <p className="text-base sm:text-xl text-gray-200 font-elegant">
                  {getSpreadName(selectedReading.spread_type)}
                </p>
              </div>

              <div className="grid gap-4 sm:gap-8">
                {selectedReading.cards.map((card, index) => (
                  <div key={index} className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 sm:p-8 border border-white/20">
                    <div className="flex flex-col md:flex-row gap-4 sm:gap-6">
                      <div className="md:w-1/3">
                        <div className="tarot-card bg-gradient-to-br from-mystical-600 to-mystical-800 rounded-xl p-4 sm:p-6 text-center">
                          <h3 className="text-lg sm:text-xl font-mystical font-bold text-white mb-2">{card.name}</h3>
                          <div className="text-5xl sm:text-6xl mb-3 sm:mb-4">🔮</div>
                          <p className="text-sm text-gray-200">
                            {card.reversed ? 'Carta Invertida' : 'Posição Normal'}
                          </p>
                        </div>
                      </div>
                      <div className="md:w-2/3">
                        <h4 className="text-xl sm:text-2xl font-mystical font-bold text-white mb-3 sm:mb-4">{card.name}</h4>
                        <p className="text-gray-200 leading-relaxed text-justify mb-3 sm:mb-4 text-sm sm:text-base">
                          {card.reversed ? card.meaning.reversed : card.meaning.upright}
                        </p>
                        <div className="bg-white/5 rounded-lg p-3 sm:p-4">
                          <h5 className="text-base sm:text-lg font-semibold text-gold-400 mb-2">Interpretação:</h5>
                          <p className="text-gray-200 leading-relaxed text-justify text-sm sm:text-base">{card.interpretation}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 sm:mt-12 bg-white/10 backdrop-blur-sm rounded-2xl p-4 sm:p-8 border border-white/20">
                <h3 className="text-xl sm:text-2xl font-mystical font-bold text-white mb-4 text-center">
                  Interpretação Geral
                </h3>
                <p className="text-gray-200 leading-relaxed text-justify text-base sm:text-lg">{selectedReading.interpretation}</p>
              </div>
            </div>
          </div>
        ) : (
          // Lista de consultas
          <div>
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-4xl font-mystical font-bold text-white mb-3 sm:mb-4">
                Suas Consultas Anteriores
              </h2>
              <p className="text-sm sm:text-xl text-gray-200 font-elegant">
                Revise suas leituras passadas e acompanhe sua jornada mística
              </p>
            </div>

            {readings.length === 0 ? (
              <div className="text-center py-12 sm:py-16">
                <div className="text-5xl sm:text-6xl mb-6">🔮</div>
                <h3 className="text-xl sm:text-2xl font-mystical font-bold text-white mb-4">
                  Nenhuma consulta realizada ainda
                </h3>
                <p className="text-gray-200 mb-8 text-sm sm:text-base">
                  Que tal fazer sua primeira consulta de tarot?
                </p>
                <Link
                  to="/dashboard"
                  className="inline-block px-8 py-3 bg-gradient-to-r from-mystical-500 to-mystical-600 text-white rounded-full font-semibold hover:from-mystical-600 hover:to-mystical-700 transition-all transform hover:scale-105"
                >
                  Fazer Consulta
                </Link>
              </div>
            ) : (
              <div className="grid gap-4 sm:gap-6">
                {readings.map((reading) => {
                  const themeData = themes[reading.theme as keyof typeof themes];
                  return (
                    <div
                      key={reading.id}
                      className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-white/20 hover:bg-white/20 transition-all"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-start sm:items-center space-x-3 sm:space-x-4">
                          <div className={`w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0 bg-gradient-to-br ${themeData?.color} rounded-full flex items-center justify-center text-xl sm:text-2xl`}>
                            {themeData?.icon}
                          </div>
                          <div>
                            <h3 className="text-base sm:text-xl font-mystical font-bold text-white">
                              Consulta de {themeData?.name}
                            </h3>
                            <p className="text-gray-300 text-sm">
                              {getSpreadName(reading.spread_type)} • {reading.cards.length} carta{reading.cards.length > 1 ? 's' : ''}
                            </p>
                            <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm text-gray-400 mt-1">
                              <div className="flex items-center space-x-1">
                                <CalendarIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                <span>{formatDate(reading.created_at)}</span>
                              </div>
                              {reading.mood && (
                                <span className="flex items-center space-x-1">
                                  <span>{moods[reading.mood]?.icon || '🙂'}</span>
                                  <span>{moods[reading.mood]?.name || reading.mood}</span>
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        <button
                          onClick={() => setSelectedReading(reading)}
                          className="flex items-center justify-center space-x-2 px-4 py-2 bg-mystical-500 hover:bg-mystical-600 text-white rounded-lg transition-colors self-start sm:self-center flex-shrink-0"
                        >
                          <EyeIcon className="w-5 h-5" />
                          <span>Ver Detalhes</span>
                        </button>
                      </div>

                      <div className="mt-4 pt-4 border-t border-white/10">
                        <p className="text-gray-200 text-sm line-clamp-2">
                          {reading.interpretation}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {readings.length > 0 && (
              <div className="text-center mt-8 sm:mt-12">
                <Link
                  to="/dashboard"
                  className="inline-block px-8 py-3 bg-gradient-to-r from-mystical-500 to-mystical-600 text-white rounded-full font-semibold hover:from-mystical-600 hover:to-mystical-700 transition-all transform hover:scale-105"
                >
                  Nova Consulta
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoryPage;
